import React, { KeyboardEvent, ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import Container from '@ui/container/container';
import InputWithOverlay from '@ui/input-with-overlay/input-with-overlay';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import Text from '@ui/text/text/text';

interface WatermarkRotateInputProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  disabled?: boolean;
}

const WatermarkRotateInput: React.FC<WatermarkRotateInputProps> = ({ value, onChange, label, disabled }) => {
  const [displayValue, setDisplayValue] = useState(value);
  const isFocused = useRef(false);

  useEffect(() => {
    if (!isFocused.current) {
      setDisplayValue(value);
    }
  }, [value]);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>): void => {
      let formatted = e.target.value.replace(/[^-0-9]/g, '');

      if (formatted.indexOf('-') > 0) {
        formatted = formatted.replace(/-/g, '');
      }

      if (formatted === '' || formatted === '-') {
        setDisplayValue(formatted);
        return;
      }

      const numericValue = Math.min(360, Math.max(-360, parseInt(formatted, 10)));
      const next = String(numericValue);
      setDisplayValue(next);
      onChange(next);
    },
    [onChange]
  );

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>): void => {
      const currentNum = parseInt(displayValue, 10) || 0;

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        const next = String(Math.min(360, currentNum + 1));
        setDisplayValue(next);
        onChange(next);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        const next = String(Math.max(-360, currentNum - 1));
        setDisplayValue(next);
        onChange(next);
      }
    },
    [displayValue, onChange]
  );

  const handleFocus = useCallback(() => {
    isFocused.current = true;
  }, []);

  const handleBlur = useCallback(() => {
    isFocused.current = false;
    if (displayValue === '' || displayValue === '-') {
      onChange('0');
      setDisplayValue('0');
    }
  }, [displayValue, onChange]);

  const degreeOffset = 24 + displayValue.length * 6;

  return (
    <InputWithOverlay
      jsxLabel={() => (
        <Text className="L2 padding-l-16 margin-b-2" design="GlobalIcoText3">
          {label}
        </Text>
      )}
      inputHeight="36px"
      innerContentClassName="padding-h-12"
      enableChildrenPointerEvents={false}
      inputWidth="100%"
      wrapperClass="flex-1"
      inputProps={{
        autoComplete: 'off',
        name: 'rotate-watermark',
        type: 'text',
        inputMode: 'text',
        maxLength: 4,
        value: displayValue,
        onKeyDown,
        onChange: handleChange,
        onFocus: handleFocus,
        onBlur: handleBlur,
        className: 'P2 padding-l-30',
        disabled,
        style: disabled ? { opacity: 0.6 } : undefined,
      }}
      renderInnerContent={() => (
        <Container className="flex flex-1 ai-center jc-space-between full-height no-pointer-events">
          <Icon name={ICONS.SKEW} size={16} />

          {displayValue !== '' && displayValue !== '-' && (
            <div
              style={{
                position: 'absolute',
                left: `${degreeOffset}px`,
                top: '-4px',
              }}
            >
              <Text className="P2" color="var(--global-ico-text-1)">
                °
              </Text>
            </div>
          )}
        </Container>
      )}
    />
  );
};

export default WatermarkRotateInput;
